﻿<template>
  <f7-block
    v-if="isPayoutCampaign"
    strong
    inset
    class="payout-kyc-warning"
  >
    <div class="kyc-warning-card bg-surface-pure rounded-xl p-4 shadow-avocash-sm display-flex flex-direction-column">
      <div class="display-flex align-items-center justify-content-space-between">
        <div class="display-flex align-items-center gap-2">
          <div class="w-8 h-8 rounded-full display-flex align-items-center justify-content-center"
               :class="verified ? 'bg-primary-fixed text-primary' : 'bg-error-container text-error'">
            <f7-icon v-if="verified" material="verified_user" :size="20" />
            <span v-else class="material-symbols-outlined" style="font-size: 20px">shield_with_heart</span>
          </div>
          <div>
            <h3 class="font-label-lg text-on-surface">{{ title }}</h3>
            <p class="font-label-sm text-on-surface-variant">{{ levelLabel }}</p>
          </div>
        </div>
        <span v-if="currentAmount > 0" class="px-2.5 py-1 rounded-full font-label-sm font-semibold uppercase tracking-wide"
              :class="payoutLevel >= 100 ? 'bg-secondary-container text-on-secondary-container' : 'bg-surface-container text-on-surface-variant'">
          {{ payoutLevel }}%
        </span>
      </div>

      <div class="kyc-warning-note display-flex align-items-flex-start">
        <f7-icon material="info" :size="20" :class="verified ? 'text-primary' : 'text-error'" class="flex-shrink-0 mt-0.5" />
        <p class="font-body-sm text-on-surface-variant leading-snug m-0">
          {{ message }}
        </p>
      </div>

      <div class="bg-surface-container-low p-3 rounded-lg display-flex flex-direction-column gap-2">
        <div class="display-flex align-items-center gap-2 font-label-sm">
          <f7-icon material="check_circle" :size="16" v-if="kycStepDone" class="text-primary" />
          <f7-icon material="radio_button_unchecked" :size="16" v-else class="text-on-surface-variant" />
          <span>{{ $t('payout.kycWarning.steps.kyc') }}</span>
          <span v-if="kycStepDone" class="text-primary font-semibold">{{ $t('payout.kycWarning.steps.done') }}</span>
          <span v-else class="text-on-surface-variant">{{ kycStatusLabel }}</span>
        </div>
        <div class="display-flex align-items-center gap-2 font-label-sm">
          <f7-icon material="check_circle" :size="16" v-if="setupStepDone" class="text-primary" />
          <f7-icon material="radio_button_unchecked" :size="16" v-else class="text-on-surface-variant" />
          <span>{{ $t('payout.kycWarning.steps.setup') }}</span>
          <span v-if="setupStepDone" class="text-primary font-semibold">{{ $t('payout.kycWarning.steps.done') }}</span>
          <span v-else class="text-on-surface-variant">{{ $t('payout.kycWarning.steps.setupPending') }}</span>
        </div>
      </div>

      <f7-button v-if="!allStepsDone && canManage" large fill raised round @click="goFinancialSetup">
        {{ $t('payout.kycWarning.action') }}
        <f7-icon material="arrow_forward" :size="18" />
      </f7-button>
    </div>
  </f7-block>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import { storeToRefs } from 'pinia'
import { useKycStore } from '../../js/pinia-stores/kyc.js'
import { useMembersStore } from '../../js/pinia-stores/members.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import { capsFor } from '../../constants/campaign-capabilities.js'
import i18n from '../../js/i18n.js'
const { t } = i18n.global

const props = defineProps({
  groupId: { type: String, required: true },
  campaign: { type: Object, default: null },
})

const kycStore = useKycStore()
const membersStore = useMembersStore()
const keycloakStore = useKeycloakStore()
const { kycStatus, isVerified } = storeToRefs(kycStore)

const supportsPayouts = computed(() => !!capsFor(props.campaign?.type)?.payouts)
const isPayoutCampaign = computed(() => supportsPayouts.value)

const currentAmount = computed(() => Number(props.campaign?.currentAmount ?? 0))
const targetAmount = computed(() => Number(props.campaign?.targetAmount ?? 0))

function clampPct(n) {
  return Math.max(0, Math.min(100, Math.round(n)))
}

const payoutLevel = computed(() => {
  const c = props.campaign
  if (!c) return 0
  if (c.targetAmount) return clampPct((currentAmount.value / targetAmount.value) * 100)
  if (c.targetQuantity) return clampPct((Number(c.currentQuantity ?? 0) / Number(c.targetQuantity)) * 100)
  if (c.maxParticipants) return clampPct((Number(c.currentParticipants ?? 0) / Number(c.maxParticipants)) * 100)
  return 0
})

const verified = computed(() => isVerified.value || kycStatus.value?.status === 'VERIFIED')

const canManage = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === keycloakStore.subject,
  )
  return viewer?.role === 'OWNER' || viewer?.role === 'ADMIN'
})

const kycStatusLabel = computed(() => {
  const s = kycStatus.value?.status || 'NOT_STARTED'
  return t('groupFinancialSetup.kycStatus.' + s)
})

const setupStepDone = computed(
  () => props.campaign?.payoutSetup?.verified === true || props.campaign?.payoutSetup?.recipientVerified === true,
)

const kycStepDone = computed(() => verified.value)

const allStepsDone = computed(() => kycStepDone.value && setupStepDone.value)

const levelLabel = computed(() => {
  if (payoutLevel.value >= 100) return t('payout.kycWarning.levelFull')
  if (payoutLevel.value > 0) return t('payout.kycWarning.levelProgress', { pct: payoutLevel.value })
  return t('payout.kycWarning.levelStarting')
})

const title = computed(() => {
  if (verified.value && setupStepDone.value) return t('payout.kycWarning.titleReady')
  if (payoutLevel.value > 0 && verified.value) return t('payout.kycWarning.titleSetup')
  return t('payout.kycWarning.titleKyc')
})

const message = computed(() => {
  if (verified.value && setupStepDone.value) return t('payout.kycWarning.messageReady')
  if (verified.value) return t('payout.kycWarning.messageSetup')
  if (payoutLevel.value >= 100) return t('payout.kycWarning.messageFull')
  if (payoutLevel.value > 0)
    return t('payout.kycWarning.messageActive', { pct: payoutLevel.value })
  return t('payout.kycWarning.messageInfo')
})

function goFinancialSetup() {
  f7.views?.main?.router?.navigate(`/groups/${props.groupId}/financial-setup`)
}

onMounted(async () => {
  try {
    await kycStore.fetchKycStatus(props.groupId)
  } catch (e) {
    // KYC status unavailable â€” the card still shows setup guidance
  }
})
</script>